import { GlobalOutlined } from '@ant-design/icons'
import { useLocalizable } from '@contexts/LocalizableContext'
import {
  fetchNotificationContents,
  saveNotificationContent,
} from '@services/NotificationContentService'
import {
  App,
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Row,
  Space,
  Steps,
  Typography,
} from 'antd'
import { FC, useEffect, useState } from 'react'
import { DEFAULT_ERROR, DEFAULT_SUCCESS } from '../../App'
import { langList } from '../../factories/Utils'
import { AvailableLocale } from '../../models/Lang'
import {
  NotificationContent,
  NotificationContentKey,
  NotificationContentLocalizable,
} from '../../models/NotificationContent'
import { SmartBanner, SmartBannerLocalizable } from '../../models/SmartBanner'
import Tenant from '../../modules/tenant/Tenant'
import { requiredRule } from '../../utils/rules'
import { IphoneCroped } from '../Iphone/IphoneCroped'
import { Localizable } from '../localizable/Localizable'
import { SmartBannerDownloadWordpressPlugin } from './smartBanner/DownloadWordpressPlugin'
import { SmartBannerViewCodeModal } from './smartBanner/ViewCodeModal'

const { Title, Text } = Typography

const CommunicationSmartBanners: FC = () => {
  const { message } = App.useApp()
  const localizable = useLocalizable()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [form] = Form.useForm()
  const title = Form.useWatch([localizable?.langCode, 'title'], form)
  const subtitle = Form.useWatch([localizable?.langCode, 'subtitle'], form)
  const buttonLabel = Form.useWatch(
    [localizable?.langCode, 'buttonLabel'],
    form
  )

  const onFinish = (values: SmartBanner) => {
    setIsLoading(true)

    const newValues: NotificationContent = {
      id: values.id,
      key: 'smart_banner',
      ...langList.reduce(
        (acc, lang) => {
          const { title, ...rest } = values[lang.code]
          return {
            ...acc,
            [lang.code]: {
              title: title,
              content: JSON.stringify(rest),
            },
          }
        },
        {} as Record<AvailableLocale, NotificationContentLocalizable>
      ),
    }

    saveNotificationContent(newValues)
      .then(() => {
        message.success(DEFAULT_SUCCESS)
      })
      .catch(() => {
        message.error(DEFAULT_ERROR)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    setIsLoading(true)
    fetchNotificationContents([NotificationContentKey.SMART_BANNER])
      .then((result) => {
        if (result.data.length === 0) return
        const notificationContent = result.data[0]
        form.setFieldsValue({
          ...notificationContent,
          ...langList.reduce(
            (acc, lang) => ({
              ...acc,
              [lang.code]: notificationContent[lang.code]
                ? {
                    title: notificationContent[lang.code].title,
                    ...JSON.parse(notificationContent[lang.code].content),
                  }
                : undefined,
            }),
            {} as Record<AvailableLocale, SmartBannerLocalizable>
          ),
        })
      })
      .catch(() => {
        message.error(DEFAULT_ERROR)
      })
      .finally(() => setIsLoading(false))
  }, [])

  return (
    <Space orientation="vertical" className="w-full">
      <Space className="w-full space-between">
        <Title level={5} className="m-0">
          <GlobalOutlined /> Promote the app on your website
        </Title>
      </Space>
      <Card>
        <Space className="w-full" orientation="vertical" size={20}>
          <Space orientation="vertical" size={10} className="w-full">
            <Title level={5} className="m-0">
              What are Smart App Banners ?
            </Title>
            <Text type="secondary">
              When a user visits your website from a mobile device, we
              automatically detect it and prompt a banner to install the
              corresponding app from the store.
            </Text>
          </Space>
          <Divider style={{ marginTop: 0, marginBottom: 10 }} />
          <Row gutter={[10, 10]}>
            <Col span={12}>
              <Steps
                orientation="vertical"
                size="small"
                current={-1}
                items={[
                  {
                    title: <Text>Edit banner content</Text>,
                    content: (
                      <Form
                        form={form}
                        disabled={isLoading}
                        style={{ marginTop: 10 }}
                        onFinish={onFinish}
                      >
                        <Space
                          orientation="vertical"
                          size={10}
                          className="w-full"
                        >
                          <Form.Item name="id" noStyle>
                            <Input type="hidden" />
                          </Form.Item>

                          <Localizable.LangSelector type="segment" />

                          <Localizable.Input
                            noStyle
                            placeholder="Title"
                            hideLangName
                            selectedLangCode={localizable?.langCode}
                            name={'title'}
                            rules={[requiredRule()]}
                          />
                          <Localizable.Input
                            noStyle
                            placeholder="Subtitle"
                            hideLangName
                            selectedLangCode={localizable?.langCode}
                            name={'subtitle'}
                            rules={[requiredRule()]}
                          />
                          <Localizable.Input
                            noStyle
                            placeholder="Button label"
                            hideLangName
                            selectedLangCode={localizable?.langCode}
                            name={'buttonLabel'}
                            rules={[requiredRule()]}
                          />
                          <Button
                            block
                            type="primary"
                            htmlType="submit"
                            disabled={isLoading}
                          >
                            Save
                          </Button>
                        </Space>
                      </Form>
                    ),
                  },
                  {
                    title: <Text>Install banner</Text>,
                    content: (
                      <Space
                        className="w-full"
                        orientation="vertical"
                        size={10}
                      >
                        <SmartBannerDownloadWordpressPlugin />
                        <SmartBannerViewCodeModal />
                      </Space>
                    ),
                  },
                ]}
              />
            </Col>
            <Col span={12}>
              <IphoneCroped hideBackground>
                <Flex
                  style={{
                    width: '100%',
                    paddingLeft: 10,
                    paddingRight: 10,
                    marginTop: 50,
                  }}
                >
                  <Card
                    style={{
                      width: '100%',
                      background: '#fff',
                      border: '0',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    }}
                    styles={{ body: { padding: 10 } }}
                  >
                    <Flex justify="space-between" align="center" gap={10}>
                      <Space>
                        <Tenant.Avatar shape="square" size={35} />
                      </Space>
                      <Flex className="w-full" vertical>
                        <Text
                          className="m-0"
                          style={{
                            color: '#000',
                            fontSize: 12,
                            padding: 0,
                          }}
                        >
                          {title}
                        </Text>
                        <Text
                          className="m-0"
                          style={{
                            color: '#000',
                            fontSize: 10,
                            padding: 0,
                          }}
                        >
                          {subtitle}
                        </Text>
                      </Flex>
                      <a
                        style={{
                          background: '#000',
                          color: '#fff',
                          padding: '5px 10px',
                          borderRadius: 10,
                          fontSize: 10,
                        }}
                      >
                        {buttonLabel}
                      </a>
                    </Flex>
                  </Card>
                </Flex>
              </IphoneCroped>
            </Col>
          </Row>
        </Space>
      </Card>
    </Space>
  )
}

type CommunicationSmartBannersType = {
  SmartBanners: typeof CommunicationSmartBanners
}

export { CommunicationSmartBanners, type CommunicationSmartBannersType }
